import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { useModuleVisibility } from '../../hooks/useModuleVisibility';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useCarichiTable } from './hooks/useCarichiTable';
import { CarichiToolbar } from './components/CarichiToolbar';
import { CarichiTable } from './components/CarichiTable';
import { ColumnsDrawer } from './components/ColumnsDrawer';
import { ManualSessionDialog } from './components/ManualSessionDialog';
import { ImportCSVWizard } from './components/csv-wizard/ImportCSVWizard';
import { GpsUploadDialog } from './components/GpsUploadDialog';
import { FiltersDrawer } from './components/FiltersDrawer';
import { ManualEntryWizard } from './components/m6-2/ManualEntryWizard';
import { ActiveFiltersChips } from './components/ActiveFiltersChips';
import { CalendarSubview } from './components/subviews/CalendarSubview';
import { ReportAthleteSubview } from './components/subviews/ReportAthleteSubview';
import { DayDetailModal } from './components/subviews/DayDetailModal';
import { baseUrl } from '../../hooks/useEndpoints';
import { groupsEndpoints } from '../../endpoints/sessions';
import { ICustomGroup } from '../../models/sessions';
import { installLeoStyles } from './theme/leoTokens';
import styles from './CarichiPage.module.scss';

interface ITeamLite {
  _id: string; name: string; users: string[]; level?: string;
}

/** Mappa subview-id → chiave feature-flag */
const CARICHI_SUBVIEW_KEYS: Record<'table' | 'calendar' | 'report', string> = {
  table: 'carichi-table',
  calendar: 'carichi-calendar',
  report: 'carichi-report',
};

export const CarichiPage: React.FC = () => {
  useEffect(() => { installLeoStyles(); }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isTabVisible } = useModuleVisibility();
  const [teams, setTeams] = useState<ITeamLite[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<ITeamLite | undefined>();
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [csvWizardOpen, setCsvWizardOpen] = useState(false);
  const [gpsUploadOpen, setGpsUploadOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [manualWizardOpen, setManualWizardOpen] = useState(false);
  /** Pre-fill per modale manuale quando aperta da Calendar cell (Addendum v8 EP2) */
  const [manualPrefill, setManualPrefill] = useState<{ date?: string; athleteId?: string; athleteName?: string }>({});
  /** Detail giorno modale (Addendum v8 EP3) */
  const [dayDetail, setDayDetail] = useState<{ open: boolean; date: string; athleteId?: string; athleteName?: string }>({ open: false, date: '' });
  const [rosterAthletes, setRosterAthletes] = useState<{ _id: string; firstName: string; lastName: string; roles?: string[] }[]>([]);
  const [customGroups, setCustomGroups] = useState<ICustomGroup[]>([]);
  const [overrideColumns, setOverrideColumns] = useState<string[] | undefined>();

  // Carico lista team del director (riuso endpoint esistente /teams)
  useEffect(() => {
    axios.get<{ docs?: ITeamLite[] } | ITeamLite[]>(`${baseUrl}/teams`).then((res) => {
      const data = res.data;
      const list = Array.isArray(data) ? data : data.docs ?? [];
      setTeams(list);
      if (list.length > 0 && !selectedTeam) setSelectedTeam(list[0]);
    }).catch((err) => console.error('Errore fetch teams', err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ctx = useCarichiTable(selectedTeam?._id);

  // Subview visibili secondo i feature-flag runtime
  const visibleSubviews = useMemo(
    () => (['table', 'calendar', 'report'] as const).filter(
      (s) => isTabVisible(CARICHI_SUBVIEW_KEYS[s])
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTabVisible]
  );

  // Se la subview attiva viene nascosta da un flag, spostati alla prima visibile
  useEffect(() => {
    if (visibleSubviews.length > 0 && !visibleSubviews.includes(ctx.subview)) {
      ctx.setSubview(visibleSubviews[0]);
    }
  }, [visibleSubviews, ctx.subview, ctx.setSubview]);

  // Carica roster atleti completo del team selezionato (per wizard CSV dropdown)
  // mongoose-query-parser: comma-separated values genera $in automaticamente
  useEffect(() => {
    if (!selectedTeam || !selectedTeam.users || selectedTeam.users.length === 0) { setRosterAthletes([]); return; }
    const ids = selectedTeam.users.join(',');
    axios.get<{ docs?: any[] } | any[]>(`${baseUrl}/users?_id=${ids}&limit=200`).then((res) => {
      const data = res.data;
      const list = Array.isArray(data) ? data : data.docs ?? [];
      setRosterAthletes(list.map((u: any) => ({
        _id: u._id, firstName: u.firstName, lastName: u.lastName,
        roles: u.roles ?? u.position ? [u.roles ?? u.position].flat().filter(Boolean) : undefined,
      })));
    }).catch(() => {
      // Fallback: solo IDs senza nomi (l'utente sceglie via dropdown vuoto)
      setRosterAthletes((selectedTeam.users ?? []).map((id) => ({ _id: id, firstName: id.slice(-4), lastName: '' })));
    });
  }, [selectedTeam]);

  // Carica custom groups per filtri drawer
  useEffect(() => {
    if (!selectedTeam) return;
    groupsEndpoints.list(selectedTeam._id).then((res) => setCustomGroups(res.data)).catch(() => setCustomGroups([]));
  }, [selectedTeam]);

  // Entry point #4 v8 — apertura via query string (?openImport=manual|csv&athleteId=&athleteName=&date=)
  useEffect(() => {
    const openImport = searchParams.get('openImport');
    if (!openImport) return;
    const athleteId = searchParams.get('athleteId') ?? undefined;
    const athleteName = searchParams.get('athleteName') ?? undefined;
    const date = searchParams.get('date') ?? undefined;
    if (athleteId || date) setManualPrefill({ athleteId, athleteName, date });
    if (openImport === 'csv') setCsvWizardOpen(true); else setManualOpen(true);
    // Pulisci la URL per evitare riapertura su refresh
    searchParams.delete('openImport'); searchParams.delete('athleteId'); searchParams.delete('athleteName'); searchParams.delete('date');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Calcolo active filters count usando filters reattivi
  const activeFilters = ctx.filters;
  const realActiveFiltersCount = useMemo(() => (
    (activeFilters.roles?.length ?? 0) +
    (activeFilters.sessionTypes?.length ?? 0) +
    (activeFilters.intensityBuckets?.length ?? 0) +
    (activeFilters.activityCategoryFilter?.length ?? 0) +
    (activeFilters.baselineFilter?.length ?? 0) +
    (activeFilters.customGroupId ? 1 : 0) +
    (activeFilters.percentileFilter ? 1 : 0)
  ), [activeFilters]);

  // Export PDF handler — usa axios per beneficiare di interceptor (auth + entity-id da Redux)
  const handleExportPdf = async () => {
    if (!selectedTeam) return;
    try {
      const response = await axios.post(`${baseUrl}/exports/pdf`, {
        teamId: selectedTeam._id, teamName: selectedTeam.name,
        tableQuery: { mode: ctx.mode, unitMode: ctx.unitMode, presetId: ctx.presetId, periodStart: ctx.period.start, periodEnd: ctx.period.end },
      }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Carichi_${selectedTeam.name}_${ctx.period.start}_${ctx.period.end}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) { alert(`Errore export PDF: ${e?.message ?? 'sconosciuto'}`); }
  };

  return (
    <div className={`leo-scope ${styles.scope}`}>
      <div className={styles.pageHeader}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Carichi Esterni</h1>
          <p className={styles.subtitle}>
            Tabella squadra · vista preset {ctx.table?.meta.presetUsed ?? '—'}
          </p>
        </div>
        <div className={styles.teamSelector}>
          <span className={styles.teamLabel}>Team</span>
          <select
            className="leo-select"
            value={selectedTeam?._id ?? ''}
            onChange={(e) => setSelectedTeam(teams.find((x) => x._id === e.target.value))}
            style={{ minWidth: 200 }}
          >
            {teams.map((t) => (<option key={t._id} value={t._id}>{t.name}</option>))}
          </select>
        </div>
      </div>

      <div className={styles.contentSurface}>
        <CarichiToolbar
          catalog={ctx.catalog}
          subview={ctx.subview} setSubview={ctx.setSubview}
          visibleSubviews={visibleSubviews}
          mode={ctx.mode} setMode={ctx.setMode}
          unitMode={ctx.unitMode} setUnitMode={ctx.setUnitMode}
          presetId={ctx.presetId}
          setPresetId={(p) => { setOverrideColumns(undefined); ctx.setPresetId(p); }}
          period={ctx.period} setPeriod={ctx.setPeriod}
          kpiMetric={ctx.kpiMetric} setKpiMetric={ctx.setKpiMetric}
          onOpenColumns={() => setColumnsOpen(true)}
          onOpenManualSession={() => setManualOpen(true)}
          onOpenManualWizard={() => setManualWizardOpen(true)}
          onOpenCsvWizard={() => setCsvWizardOpen(true)}
          onOpenGpsUpload={() => setGpsUploadOpen(true)}
          onOpenFilters={() => setFiltersOpen(true)}
          onExportPdf={handleExportPdf}
          activeFiltersCount={realActiveFiltersCount}
        />

        <ActiveFiltersChips filters={ctx.filters} onChange={ctx.setFilters} customGroups={customGroups} />

        {ctx.subview === 'table' && ctx.table && ctx.table.meta.sampleSize === 0 && (
          <div className={styles.warningBanner}>
            Nessuna sessione nel periodo per questo team. Clicca <strong><Download size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} /> Importa</strong> per inserire un dato manuale.
          </div>
        )}

        {ctx.subview === 'table' && (
          <CarichiTable table={ctx.table} catalog={ctx.catalog} isLoading={ctx.isLoading} isError={ctx.isError} />
        )}

        {ctx.subview === 'calendar' && selectedTeam && (
          <CalendarSubview
            teamId={selectedTeam._id}
            rosterAthletes={rosterAthletes}
            period={ctx.period}
            onCellClick={(date, athleteId) => {
              const ath = rosterAthletes.find((a) => a._id === athleteId);
              setDayDetail({ open: true, date, athleteId, athleteName: ath ? `${ath.firstName} ${ath.lastName}` : undefined });
            }}
          />
        )}

        {ctx.subview === 'report' && (
          <ReportAthleteSubview rosterAthletes={rosterAthletes} period={ctx.period} />
        )}
      </div>

      <ColumnsDrawer open={columnsOpen} onClose={() => setColumnsOpen(false)} catalog={ctx.catalog}
        activeColumns={overrideColumns ?? ctx.activeColumns} onApply={(cols) => setOverrideColumns(cols)} />

      {selectedTeam && (
        <ManualSessionDialog open={manualOpen}
          onClose={() => { setManualOpen(false); setManualPrefill({}); }}
          teamId={selectedTeam._id} rosterAthleteIds={selectedTeam.users ?? []}
          defaultDate={manualPrefill.date ?? ctx.period.end}
          prefillAthleteId={manualPrefill.athleteId} prefillAthleteName={manualPrefill.athleteName} />
      )}

      {selectedTeam && (
        <ImportCSVWizard open={csvWizardOpen} onClose={() => setCsvWizardOpen(false)} catalog={ctx.catalog}
          teamId={selectedTeam._id} rosterAthletes={rosterAthletes} onCompleted={() => ctx.refetch()} />
      )}

      {selectedTeam && (
        <GpsUploadDialog open={gpsUploadOpen} onClose={() => setGpsUploadOpen(false)}
          teamId={selectedTeam._id} rosterAthletes={rosterAthletes} onCompleted={() => ctx.refetch()} />
      )}

      <FiltersDrawer open={filtersOpen} onClose={() => setFiltersOpen(false)}
        filters={ctx.filters} onApply={(f) => ctx.setFilters(f)}
        rosterAthletes={rosterAthletes} customGroups={customGroups.map((g) => ({ _id: g._id, name: g.name }))} />

      {selectedTeam && (
        <ManualEntryWizard
          open={manualWizardOpen}
          onClose={() => setManualWizardOpen(false)}
          teamId={selectedTeam._id}
          teamName={selectedTeam.name}
          onCompleted={() => ctx.refetch()}
        />
      )}

      <DayDetailModal open={dayDetail.open} onClose={() => setDayDetail({ ...dayDetail, open: false })}
        date={dayDetail.date} athleteId={dayDetail.athleteId} athleteName={dayDetail.athleteName}
        onImportClick={() => { setManualPrefill({ date: dayDetail.date, athleteId: dayDetail.athleteId, athleteName: dayDetail.athleteName }); setManualOpen(true); }} />
    </div>
  );
};
